// TEN19 (docs/build-briefs/TEN19-publish-recipes.md; DEC-342 Q7, P0.5): the location's "From <network name>" area in
// Settings > Recipes. Lists the recipes the franchise network offers this location with Accept and Decline (Optional) and
// Map ingredients. Map ingredients opens an inline step under the offer (no overlay): one picker of the location's own items
// per ingredient and a price. Nothing is a drink until the map is saved. Wiring only: every decision is in
// networkRecipeOffersModel.ts, and the API calls are handed in by admin.ts so this file holds no session logic. All text
// goes in with textContent.

import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import { parseDollarsToCentsExact } from "./money.ts";
import { buildCreateConfirm, mapAcceptLabel } from "./createItemsConfirm.ts";
import {
  CREATE_BLOCKED_MESSAGE,
  CREATE_CHOICE,
  CREATE_CHOICE_LABEL,
  CREATE_CONFIRM_INTRO,
  createCandidatesOf,
  offersCreate,
  acceptedMessage,
  clonedMessage,
  declinedMessage,
  describeRecipeOfferFailure,
  emptyMapDraft,
  isRecipeOffersShape,
  itemChoices,
  offersFrom,
  OWN_RECIPE_OFFERS_HEADING,
  mapDraftToBody,
  mappedMessage,
  RECIPE_OFFERS_LOAD_FAILED,
  recipeOfferViews,
  recipeOffersHeading,
  SHARED_RECIPE_OFFERS_HEADING,
  showsRecipeOffers,
  slotLabel,
  type ItemChoice,
  type MapDraft,
  type RecipeOfferView,
  type RecipeOffersResponseDto,
} from "./networkRecipeOffersModel.ts";

interface Reply<T> {
  status: number;
  body: T;
}

type Failure = { error?: string; message?: string } | null;

export interface NetworkRecipeOffersDeps {
  section: HTMLElement;
  title: HTMLElement;
  list: HTMLElement;
  status: HTMLElement;
  fetchOffers: () => Promise<Reply<unknown>>;
  answer: (publicationId: string, kind: "accept" | "decline") => Promise<Reply<Failure>>;
  /** The location's own active inventory items for the pickers. */
  fetchItems: () => Promise<Reply<unknown>>;
  map: (
    publicationId: string,
    body: {
      priceCents?: number;
      expectedContentRevision?: number;
      mappings: { position: number; liquorProductId: string }[];
      createSlots?: number[];
      approvedCreate?: string[];
      addonMappings?: { optionKey: string; inventoryItemId: string; quantityPerUse?: string; unit?: string; priceAdjustmentCents?: number }[];
    },
  ) => Promise<Reply<Failure>>;
  /** REC7-S2: clones a recipe another franchisee shared (same body as map). Needed only by the `shared` source. */
  clone?: (
    publicationId: string,
    body: {
      priceCents: number;
      mappings: { position: number; liquorProductId: string }[];
      addonMappings?: { optionKey: string; inventoryItemId: string; quantityPerUse?: string; unit?: string; priceAdjustmentCents?: number }[];
    },
  ) => Promise<Reply<Failure>>;
  /** Called after a successful map, so the Drinks list re-reads and shows the new drink. */
  onMapped: () => void;
  onAnswered?: () => void;
  toast: (message: string, variant: "success") => void;
  /** False once the session or screen the request began under is gone; a late reply then does nothing. */
  isCurrent: () => boolean;
  scope?: () => string | null;
  /** REC7-S1: which recipes this area lists. Default `network` (TEN19); `own` lists the ones this franchisee published. */
  source?: "network" | "own" | "shared" | "all";
  onRender?: (rows: HTMLElement[], response: RecipeOffersResponseDto | null) => void;
}

export interface NetworkRecipeOffersControl {
  refresh: () => Promise<void>;
  reset: () => void;
}

function itemsFrom(body: unknown): ItemChoice[] | null {
  if (typeof body !== "object" || body === null) return null;
  const list = (body as Record<string, unknown>)["liquorProducts"];
  if (!Array.isArray(list)) return null;
  const out: ItemChoice[] = [];
  for (const row of list) {
    if (typeof row !== "object" || row === null) return null;
    const r = row as Record<string, unknown>;
    if (typeof r["id"] !== "string" || typeof r["name"] !== "string") return null;
    if (r["active"] === false) continue;
    out.push({ id: r["id"], name: r["name"], addonEnabled: r["addon_enabled"] === true });
  }
  return itemChoices(out);
}

export function createNetworkRecipeOffers(
  deps: NetworkRecipeOffersDeps,
): NetworkRecipeOffersControl {
  const source = deps.source ?? "network";
  let response: RecipeOffersResponseDto | null = null;
  let busy = false;
  let mappingId: string | null = null;
  let mappingMode: "map" | "clone" = "map";
  let mapDraft: MapDraft = emptyMapDraft();
  /** MAPCREATE1: true while the list of items to be added is shown for approval. Nothing is created until it is accepted. */
  let confirming = false;
  /** The ingredient names ticked on that list. Starts empty every time the list is shown. */
  let approvedKeys = new Set<string>();
  let items: ItemChoice[] = [];
  let addonSelections: Record<string, string> = {};
  let ingredientDrafts: Record<string,{quantity:string;unit:string;price:string}> = {};
  let renderedRows: HTMLElement[] = [];
  let generation = 0;
  let refreshSequence = 0;
  let responseScope: string | null | undefined;
  const scopeNow = (): string | null | undefined => deps.scope?.();
  const isCurrentOperation = (version: number, scope: string | null | undefined): boolean =>
    generation === version && deps.isCurrent() && scopeNow() === scope;
  const hasCurrentResponse = (): boolean => response !== null && deps.isCurrent() && responseScope === scopeNow();

  function reset(): void {
    generation++; refreshSequence++; busy = false; responseScope = undefined;
    response = null;
    mappingId = null;
    mapDraft = emptyMapDraft();
    confirming = false; approvedKeys = new Set();
    addonSelections = {}; ingredientDrafts = {};
    items = [];
    deps.section.hidden = true;
    deps.list.replaceChildren();
    deps.status.textContent = "";
    renderedRows = [];
    deps.onRender?.([], null);
  }

  function setBusy(value: boolean): void {
    busy = value;
    for (const row of renderedRows)
      for (const button of row.querySelectorAll("button")) button.disabled = value;
  }

  function renderConfirmPanel(view: RecipeOfferView): HTMLElement {
    const panel = document.createElement("div");
    panel.className = "admin-network-recipe-map";
    panel.setAttribute("data-testid", "admin-network-recipe-map");
    const error = document.createElement("p");
    error.className = "admin-form-error";
    error.setAttribute("data-testid", "admin-network-recipe-map-error");
    error.setAttribute("role", "alert");
    const candidates = createCandidatesOf(view.slots, mapDraft).map((candidate) => ({ ...candidate, leftOutNote: "" }));
    const saveGeneration = generation, saveScope = responseScope;
    const { card, actions } = buildCreateConfirm({
      candidates,
      approved: approvedKeys,
      busy,
      testidPrefix: "admin-network-recipe-map-confirm",
      intro: CREATE_CONFIRM_INTRO,
      acceptLabel: mapAcceptLabel,
      // An ingredient left unticked is unmapped, and a partial map writes nothing: so every item must be ticked.
      blockedReason: (count) => (count < candidates.length ? CREATE_BLOCKED_MESSAGE : null),
      showLeftOut: false,
      classes: { primary: "btn-primary btn-compact", secondary: "btn-secondary btn-compact", actions: "admin-network-recipe-map-actions" },
      onToggle: (key, ticked) => {
        if (ticked) approvedKeys.add(key);
        else approvedKeys.delete(key);
        render();
      },
      onBack: () => {
        confirming = false;
        approvedKeys = new Set();
        render();
      },
      onAccept: () => {if (isCurrentOperation(saveGeneration, saveScope)) void saveMap(view, error);},
    });
    panel.append(card, error, actions);
    return panel;
  }

  function renderMapPanel(view: RecipeOfferView): HTMLElement {
    if (confirming) return renderConfirmPanel(view);
    const panel = document.createElement("div");
    panel.className = "admin-network-recipe-map";
    panel.setAttribute("data-testid", "admin-network-recipe-map");
    for (const slot of view.slots) {
      const row = document.createElement("div");
      row.className = "admin-network-recipe-map-row";
      row.setAttribute("data-testid", "admin-network-recipe-map-row");
      const label = document.createElement("label");
      label.className = "admin-network-recipe-map-slot";
      const selectId = `admin-network-recipe-map-${view.publicationId}-${slot.position}`;
      label.htmlFor = selectId;
      label.textContent = slotLabel(slot);
      const select = document.createElement("select");
      select.className = "field-input";
      select.id = selectId;
      select.setAttribute("data-testid", "admin-network-recipe-map-item");
      select.setAttribute("data-slot-name", slot.name);
      const none = document.createElement("option");
      none.value = "";
      none.textContent = "Choose an item";
      select.append(none);
      if (offersCreate(slot, mappingMode === "clone")) {
        // MAPCREATE1 (DEC-594): never the default. Choosing it asks first, with the list of items it would add.
        const create = document.createElement("option");
        create.value = CREATE_CHOICE;
        create.textContent = CREATE_CHOICE_LABEL;
        select.append(create);
      }
      for (const item of items) {
        const option = document.createElement("option");
        option.value = item.id;
        option.textContent = item.name;
        select.append(option);
      }
      select.value = mapDraft.selected[slot.position] ?? "";
      const suggestion = document.createElement("small");
      suggestion.textContent = "Confirmed compatible catalog match. Review before saving.";
      suggestion.hidden = !slot.suggestedItemId || select.value !== slot.suggestedItemId;
      select.addEventListener("change", () => {
        mapDraft = {
          ...mapDraft,
          selected: { ...mapDraft.selected, [slot.position]: select.value },
        };
        suggestion.hidden = !slot.suggestedItemId || select.value !== slot.suggestedItemId;
      });
      row.append(label, select, suggestion);
      panel.append(row);
    }
    for (const option of view.addons?.options ?? []) {
      const row = document.createElement("div");
      row.className = "admin-network-recipe-map-row";
      const label = document.createElement("label");
      label.className = "admin-network-recipe-map-slot";
      const group = view.addons?.groups.find((candidate) => candidate.key === option.groupKey);
      label.textContent = group === undefined ? option.label : `${group.label}: ${option.label}`;
      const select = document.createElement("select");
      select.className = "field-input";
      select.setAttribute("aria-label", `Ingredient ${option.label}`);
      const none = document.createElement("option");
      none.value = "";
      none.textContent = "Choose an ingredient";
      select.append(none);
      for (const item of items) {
        const pick = document.createElement("option");
        pick.value = item.id;
        pick.textContent = item.name;
        select.append(pick);
      }
      select.value = addonSelections[option.key] ?? "";
      select.addEventListener("change", () => {
        addonSelections[option.key] = select.value;
      });
      label.append(select);
      row.append(label);
      const draft = ingredientDrafts[option.key] ??= {quantity:option.quantityPerUse ?? "",unit:option.unit ?? "",price:""};
      const amountLabel = document.createElement("label"); amountLabel.textContent = "Amount";
      const amount = document.createElement("input"); amount.type = "text"; amount.inputMode="decimal"; amount.className="field-input";
      amount.value=draft.quantity; amount.readOnly=option.quantityPerUse != null; amount.oninput=()=>{draft.quantity=amount.value;}; amountLabel.append(amount);
      const unitLabel=document.createElement("label"); unitLabel.textContent="Unit";
      const unit=document.createElement("select");unit.className="field-input";
      for(const [value,text] of [["","Choose unit"],["oz","oz"],["unit","each"]]) {const pick=document.createElement("option");pick.value=value!;pick.textContent=text!;unit.append(pick);}
      unit.value=draft.unit;unit.disabled=option.unit != null;unit.onchange=()=>{draft.unit=unit.value;};unitLabel.append(unit);
      const adjustmentLabel=document.createElement("label");adjustmentLabel.textContent="Price adjustment ($)";
      const adjustment=document.createElement("input");adjustment.type="text";adjustment.inputMode="decimal";adjustment.className="field-input";
      adjustment.value=draft.price;adjustment.oninput=()=>{draft.price=adjustment.value;};adjustmentLabel.append(adjustment);
      row.append(amountLabel,unitLabel,adjustmentLabel);
      panel.append(row);
    }
    const priceRow = document.createElement("div");
    priceRow.className = "admin-network-recipe-map-price";
    const priceLabel = document.createElement("label");
    const priceId = `admin-network-recipe-map-price-${view.publicationId}`;
    priceLabel.htmlFor = priceId;
    priceLabel.textContent = "Price";
    const price = document.createElement("input");
    price.type = "text";
    price.inputMode = "decimal";
    price.className = "field-input";
    price.id = priceId;
    price.autocomplete = "off";
    price.setAttribute("data-testid", "admin-network-recipe-map-price");
    price.value = mapDraft.priceText;
    price.addEventListener("input", () => {
      mapDraft = { ...mapDraft, priceText: price.value };
    });
    priceRow.append(priceLabel, price);
    priceRow.hidden = view.existingDrink === true && mappingMode !== "clone";
    const error = document.createElement("p");
    error.className = "admin-form-error";
    error.setAttribute("data-testid", "admin-network-recipe-map-error");
    error.setAttribute("role", "alert");
    const actions = document.createElement("div");
    actions.className = "admin-network-recipe-map-actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn-secondary btn-compact";
    cancel.setAttribute("data-testid", "admin-network-recipe-map-cancel");
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => {
      mappingId = null;
      confirming = false; approvedKeys = new Set();
      render();
    });
    const save = document.createElement("button");
    save.type = "button";
    save.className = "btn-primary btn-compact";
    save.setAttribute("data-testid", "admin-network-recipe-map-save");
    save.textContent = mappingMode === "clone" ? "Save clone" : view.existingDrink === true ? "Save mapping" : "Add to menu";
    const saveGeneration = generation, saveScope = responseScope;
    save.addEventListener("click", () => {if (isCurrentOperation(saveGeneration, saveScope)) void saveMap(view, error);});
    actions.append(cancel, save);
    panel.append(priceRow, error, actions);
    return panel;
  }

  function renderOffer(view: RecipeOfferView): HTMLElement {
    const item = document.createElement(source === "all" ? "div" : "li");
    item.className = "admin-network-offer admin-network-recipe-offer";
    item.dataset["publicationId"] = view.publicationId;
    item.dataset["recipeName"] = view.name;
    item.setAttribute("data-testid", "admin-network-recipe-offer");
    item.setAttribute("data-publication-name", view.name);

    const text = document.createElement("div");
    text.className = "admin-network-offer-text";
    const name = document.createElement("span");
    name.className = "admin-network-offer-name";
    name.setAttribute("data-testid", "admin-network-recipe-offer-name");
    name.textContent = view.name;
    const terms = document.createElement("span");
    terms.className = "admin-network-offer-terms";
    terms.setAttribute("data-testid", "admin-network-recipe-offer-terms");
    terms.textContent = view.detailText;
    const locations = document.createElement("span");
    locations.className = "admin-network-offer-terms";
    locations.setAttribute("data-testid", "admin-network-recipe-offer-locations");
    locations.textContent = view.locationsText;
    text.append(name);
    if (view.fromText !== "") {
      const from = document.createElement("span");
      from.className = "admin-network-offer-terms";
      from.setAttribute("data-testid", "admin-network-recipe-offer-from");
      from.textContent = view.fromText;
      text.append(from);
    }
    text.append(terms, locations);
    // RECUX2: the franchisor's build instructions, shown read only. A location never edits them.
    if (view.buildText !== undefined) {
      const build = document.createElement("span");
      build.className = "admin-network-offer-terms";
      build.setAttribute("data-testid", "admin-network-recipe-offer-build");
      build.textContent = view.buildText;
      text.append(build);
    }
    if (view.steps !== undefined) {
      const steps = document.createElement("ol");
      steps.className = "admin-network-recipe-build";
      steps.setAttribute("data-testid", "admin-network-recipe-offer-steps");
      for (const step of view.steps) {
        const line = document.createElement("li");
        line.textContent = step;
        steps.append(line);
      }
      text.append(steps);
    }

    const state = document.createElement("span");
    state.className = `so-badge so-badge-${view.tone} admin-network-offer-state`;
    state.setAttribute("data-testid", "admin-network-recipe-offer-state");
    const dot = document.createElement("span");
    dot.className = "so-badge-dot";
    dot.setAttribute("aria-hidden", "true");
    state.append(dot, view.stateLabel);

    const actions = document.createElement("div");
    actions.className = "admin-network-offer-actions";
    const button = (
      label: string,
      testId: string,
      className: string,
      onClick: () => void,
    ): void => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = className;
      b.setAttribute("data-testid", testId);
      b.textContent = label;
      b.setAttribute("aria-label", `${label} ${view.name}`);
      const buttonGeneration = generation, buttonScope = responseScope;
      b.addEventListener("click", () => {if (isCurrentOperation(buttonGeneration, buttonScope)) onClick();});
      actions.append(b);
    };
    if (view.canAccept) {
      button(
        "Accept",
        "admin-network-recipe-offer-accept",
        "btn-primary btn-compact",
        () => void answer(view, "accept"),
      );
    }
    if (view.canMap) {
      button(
        "Map ingredients",
        "admin-network-recipe-offer-map",
        "btn-primary btn-compact",
        () => void openMap(view),
      );
    }
    if (view.canClone && deps.clone !== undefined) {
      button(
        "Clone",
        "admin-network-recipe-offer-clone",
        "btn-secondary btn-compact",
        () => void openMap(view, "clone"),
      );
    }
    if (view.canDecline) {
      button(
        "Decline",
        "admin-network-recipe-offer-decline",
        "btn-secondary btn-compact",
        () => void answer(view, "decline"),
      );
    }
    item.append(text, state, actions);
    if (
      mappingId === view.publicationId &&
      ((mappingMode === "map" && view.canMap) || (mappingMode === "clone" && view.canClone))
    ) {
      item.append(renderMapPanel(view));
    }
    return item;
  }

  function render(): void {
    if (response === null || (source !== "all" && !showsRecipeOffers(response, source))) {
      reset();
      return;
    }
    const offers =
      source === "all"
        ? response.offers.filter((offer) => offer.state !== "adopted")
        : offersFrom(response, source);
    const views = recipeOfferViews(offers);
    if (source === "all") {
      for (const view of views) {
        const offer = offers.find((entry) => entry.publicationId === view.publicationId);
        view.fromText =
          offer?.source === "network"
            ? "From corporate"
            : offer?.source === "own"
              ? "Your recipe"
              : view.fromText;
      }
    }
    renderedRows = views.map((view) => {
      const row = renderOffer(view);
      row.dataset["recipeCategory"] =
        offers.find((entry) => entry.publicationId === view.publicationId)?.category ?? "";
      return row;
    });
    deps.title.textContent = headingFor(response);
    deps.list.replaceChildren(...renderedRows);
    deps.section.hidden = deps.onRender !== undefined;
    deps.onRender?.(renderedRows, response);
  }

  function headingFor(current: RecipeOffersResponseDto | null): string {
    if (source === "all") return "Recipes";
    if (source === "own") return OWN_RECIPE_OFFERS_HEADING;
    if (source === "shared") return SHARED_RECIPE_OFFERS_HEADING;
    return recipeOffersHeading(
      current === null || current.network === null ? "" : current.network.name,
    );
  }

  async function openMap(view: RecipeOfferView, mode: "map" | "clone" = "map"): Promise<void> {
    if (busy || !hasCurrentResponse()) return;
    const version = generation, scope = scopeNow();
    setBusy(true);
    deps.status.textContent = "";
    const result = await deps.fetchItems();
    if (!isCurrentOperation(version, scope)) return;
    setBusy(false);
    const found = result.status === 200 ? itemsFrom(result.body) : null;
    if (found === null) {
      deps.status.textContent = describeRecipeOfferFailure(result.status, null);
      return;
    }
    items = found;
    mapDraft = emptyMapDraft();
    confirming = false; approvedKeys = new Set();
    for (const slot of view.slots) {
      if (slot.suggestedItemId && items.some(item => item.id === slot.suggestedItemId))
        mapDraft.selected[slot.position] = slot.suggestedItemId;
    }
    addonSelections = {}; ingredientDrafts = {};
    mappingId = view.publicationId;
    mappingMode = mode;
    render();
    renderedRows
      .find((row) => row.dataset["publicationId"] === view.publicationId)
      ?.querySelector<HTMLSelectElement>("[data-testid='admin-network-recipe-map-item']")
      ?.focus();
  }

  async function saveMap(view: RecipeOfferView, error: HTMLElement): Promise<void> {
    if (busy || !hasCurrentResponse()) return;
    const version = generation, scope = scopeNow();
    const made = mapDraftToBody(view.slots, mapDraft, view.existingDrink === true && mappingMode !== "clone");
    if (!made.ok) {
      error.textContent = made.message;
      return;
    }
    const addonMappings: {optionKey:string;inventoryItemId:string;quantityPerUse:string;unit:string;priceAdjustmentCents:number}[]=[];
    for (const option of view.addons?.options ?? []) {
      const draft=ingredientDrafts[option.key];
      let price:number|null=null;
      try {if(draft!==undefined) price=parseDollarsToCentsExact(draft.price,"Price adjustment");}
      catch {error.textContent="Enter the ingredient price adjustment in dollars and cents.";return;}
      if (!addonSelections[option.key] || draft === undefined || !/^\d+(?:\.\d{1,4})?$/.test(normalizeLeadingDot(draft.quantity)) || Number(normalizeLeadingDot(draft.quantity))<=0 ||
          (draft.unit!=="oz" && draft.unit!=="unit") || price===null || price<0) {
        error.textContent="Choose each ingredient and set its amount, unit and price adjustment.";return;
      }
      addonMappings.push({optionKey:option.key,inventoryItemId:addonSelections[option.key]!,quantityPerUse:normalizeLeadingDot(draft.quantity),unit:draft.unit,priceAdjustmentCents:price});
    }
    const wantedCreates = createCandidatesOf(view.slots, mapDraft);
    if (wantedCreates.length > 0 && !confirming) {
      // Ask first: show the list of items it would add, every one unticked. Nothing is created until the person ticks and accepts.
      confirming = true;
      approvedKeys = new Set();
      render();
      return;
    }
    // Only what was ticked is approved; the server refuses a create whose names are not exactly this list.
    const approvedCreate = wantedCreates.filter((candidate) => approvedKeys.has(candidate.key)).map((candidate) => candidate.name);
    const body = { ...made.body, ...(wantedCreates.length > 0 ? { approvedCreate } : {}), ...(view.contentRevision === undefined ? {} : {expectedContentRevision: view.contentRevision}), ...(addonMappings.length > 0 ? { addonMappings } : {}) };
    error.textContent = "";
    setBusy(true);
    const cloning = mappingMode === "clone" && deps.clone !== undefined;
    if (cloning && body.priceCents === undefined) {setBusy(false); return;}
    const result = cloning
      ? await (deps.clone as NonNullable<typeof deps.clone>)(view.publicationId, {...body, priceCents: body.priceCents as number})
      : await deps.map(view.publicationId, body);
    if (!isCurrentOperation(version, scope)) return;
    setBusy(false);
    if (result.status === 200 || result.status === 201) {
      mappingId = null;
      mapDraft = emptyMapDraft();
      confirming = false; approvedKeys = new Set();
      addonSelections = {}; ingredientDrafts = {};
      deps.toast(cloning ? clonedMessage(view.name) : view.existingDrink === true ? `${view.name} mapping saved.` : mappedMessage(view.name), "success");
      await refresh();
      if (!isCurrentOperation(version, scope)) return;
      deps.onMapped();
      return;
    }
    error.textContent = describeRecipeOfferFailure(result.status, result.body);
  }

  async function answer(view: RecipeOfferView, kind: "accept" | "decline"): Promise<void> {
    if (busy || !hasCurrentResponse()) return;
    const version = generation, scope = scopeNow();
    setBusy(true);
    deps.status.textContent = "";
    const result = await deps.answer(view.publicationId, kind);
    if (!isCurrentOperation(version, scope)) return;
    setBusy(false);
    if (result.status === 200 || result.status === 201) {
      deps.toast(
        kind === "accept" ? acceptedMessage(view.name) : declinedMessage(view.name),
        "success",
      );
      deps.onAnswered?.();
      if (kind === "decline" && mappingId === view.publicationId) mappingId = null;
      await refresh();
      return;
    }
    deps.status.textContent = describeRecipeOfferFailure(result.status, result.body);
  }

  async function refresh(): Promise<void> {
    if (!deps.isCurrent()) return;
    const version = generation, scope = scopeNow(), sequence = ++refreshSequence;
    const result = await deps.fetchOffers();
    if (!isCurrentOperation(version, scope) || sequence !== refreshSequence) return;
    if (result.status !== 200 || !isRecipeOffersShape(result.body)) {
      if (result.status === 403) {
        reset();
        return;
      }
      deps.title.textContent =
        source === "network" ? "From your franchise network" : headingFor(null);
      deps.list.replaceChildren();
      deps.status.textContent = RECIPE_OFFERS_LOAD_FAILED;
      deps.section.hidden = deps.onRender !== undefined;
      renderedRows = [];
      deps.onRender?.([], null);
      return;
    }
    deps.status.textContent = "";
    response = result.body;
    responseScope = scope;
    render();
  }

  return { refresh, reset };
}
